import { div, esc, kr, nf1, num, pct } from '../calc/format.js';
import { CFG, WEEKDAYS } from '../calc/model.js';
import { supplierGap } from '../calc/stats.js';
import { change } from '../calc/periods.js';
import { customerFiltered } from './customers.js';
import { NO_COST_TEXT, el } from './dom.js';
import { state } from './state.js';

export function renderCompare(s, has){
  const card  = el('compareCard');
  const empty = el('compareEmpty');
  const p = state.prev;

  if(!has){
    card.style.display = 'none';
    empty.style.display = 'none';
    return;
  }
  if(!p){
    // Hellre förklara varför jämförelsen saknas än att bara dölja kortet
    card.style.display = 'none';
    empty.style.display = '';
    el('compareEmptyText').textContent = state.compareMode === 'yoy'
      ? 'Filen innehåller ingen försäljning från samma period förra året. Exportera en längre period, eller jämför med föregående period i stället.'
      : 'Det finns ingen försäljning i perioden närmast före den du valt. Det är väntat om du tittar på den tidigaste perioden i filen.';
    return;
  }
  card.style.display = '';
  empty.style.display = 'none';
  el('compareSub').textContent = `Den valda perioden mot ${p.label}`;

  // [etikett, nyckel, formaterare, enhetslöst mått, ökning är negativ]
  const metrics = [
    ['Omsättning',            'revenue',        kr,   false, false],
    ['Bruttovinst',           'profit',         kr,   false, false],
    ['Bruttomarginal',        'margin',         pct,  true,  false],
    ['Antal köp',             'tx',             num,  false, false],
    ['Snittköp',              'aov',            kr,   false, false],
    ['Artiklar per köp',      'itemsPerTx',     v => nf1.format(v).replace('.',','), false, false],
    ['Snittpris per artikel', 'avgUnitPrice',   kr,   false, false],
    ['Köp med flera artiklar','multiLineShare', pct,  true,  false],
    ['Returandel',            'returnShare',    pct,  true,  true],
    ['Rabattgrad',            'discountShare',  pct,  true,  true],
    ['Köp med kund',          'custShare',      pct,  true,  false],
    ['Försäljning med kund',  'custRevenueShare', pct, true, false]
  ];

  // Utan inköpspris i filen finns ingen bruttovinst eller marginal att jämföra,
  // och andelen med kund säger bara något när filen har kunder och urvalet
  // inte redan är valda kunder
  const custOk = state.hasCustomers && !customerFiltered();
  const shown = metrics.filter(m =>
    (state.hasCost || (m[1] !== 'profit' && m[1] !== 'margin')) &&
    (custOk || (m[1] !== 'custShare' && m[1] !== 'custRevenueShare')));

  let html = `<thead><tr><th>Nyckeltal</th><th class="num">Vald period</th>
    <th class="num">${esc(p.label.charAt(0).toUpperCase() + p.label.slice(1))}</th>
    <th class="num">Förändring</th></tr></thead><tbody>`;

  for(const [label, key, fmt, isPoints, invert] of shown){
    const now = s[key], before = p.stats[key];
    let cell = '–';

    if(isPoints){
      const d = now - before;
      if(isFinite(d) && !(now === 0 && before === 0)){
        const dir  = Math.abs(d) < 0.05 ? '' : (d > 0 ? 'up' : 'down');
        const good = invert ? d < 0 : d > 0;
        cell = dir
          ? `<span class="delta ${dir} ${good ? 'good' : 'bad'}">${esc((d > 0 ? '+' : '−') + nf1.format(Math.abs(d)).replace('.',',') + ' p.e.')}</span>`
          : 'oförändrat';
      }
    }else{
      const c = change(now, before);
      if(c !== null){
        const dir  = Math.abs(c) < 0.5 ? '' : (c > 0 ? 'up' : 'down');
        const good = invert ? c < 0 : c > 0;
        cell = dir
          ? `<span class="delta ${dir} ${good ? 'good' : 'bad'}">${esc((c > 0 ? '+' : '−') + nf1.format(Math.abs(c)).replace('.',',') + ' %')}</span>`
          : 'oförändrat';
      }
    }

    html += `<tr>
      <td class="name">${esc(label)}</td>
      <td class="num">${esc(fmt(now))}</td>
      <td class="num">${esc(fmt(before))}</td>
      <td class="num">${cell}</td>
    </tr>`;
  }

  html += `</tbody><tfoot><tr><td colspan="4" style="color:var(--ink-soft);font-size:13px;padding-top:14px;border-bottom:none">
    ${esc(compareNote(p))}
    </td></tr></tfoot>`;

  el('tCompare').innerHTML = html;
}

/** Förklaringen under periodjämförelsen: vad som är lika, och vad som inte är det. */
export function compareNote(p){
  const scope = `butik, kassa, kassör, ${state.hasCustomers ? 'kund, ' : ''}artikelgrupp och leverantör`;
  const parts = [state.dayTime
    ? `Jämförelseperioden har samma urval av veckodagar, klockslag, ${scope} som den valda perioden.`
    : `Jämförelseperioden har samma urval av ${scope} som den valda perioden.`];
  if(p.partial){
    parts.push(state.compareMode === 'yoy'
      ? 'Den valda månaden är ännu inte komplett, så jämförelsen är begränsad till lika många dagar in i samma månad förra året.'
      : 'Den valda månaden är ännu inte komplett, så jämförelsen är begränsad till lika många dagar in i föregående månad.');
  }else if(p.basis === 'weekday'){
    parts.push(state.compareMode === 'yoy'
      ? 'Med veckodagar valda jämförs med samma veckodagar 52 veckor tidigare, så att en fredag jämförs med en fredag.'
      : 'Med veckodagar valda flyttas jämförelsen hela veckor bakåt, så att den har exakt samma veckodagar.');
  }else if(!p.dayCounts){
    parts.push('Den har också samma längd.');
  }
  const c = p.dayCounts;
  if(c && c.now !== c.before){
    const single = state.weekdays.length === 1;
    const what = single ? WEEKDAY_PLURAL_LOWER[state.weekdays[0]] : 'valda veckodagar';
    parts.push(`Den valda månaden har ${num(c.now)} ${what} och jämförelsemånaden ${num(c.before)}. ` +
      'Summor som omsättning och antal köp påverkas av det; snittköp och marginal gör det inte.');
  }
  return parts.join(' ');
}

const WEEKDAY_PLURAL_LOWER = WEEKDAYS.map(w => w.toLowerCase() + 'ar');

/* ---- Insikter ---- */
export function renderInsights(s, has){
  const box = el('insightList');
  if(!has){
    box.innerHTML = `<div class="empty"><strong>Inga kvitton i urvalet</strong>Välj en period som innehåller försäljning.</div>`;
    return;
  }

  const notes = [];

  // Datakvalitet först: utan inköpspris blir marginalen missvisande. Saknar
  // varje artikel i filen inköpspris visas varken vinst eller marginal, och
  // de iakttagelser som bygger på dem hoppas över.
  if(!state.hasCost){
    notes.push({tone:'warn', h:'Inköpspris saknas på alla artiklar',
      p:`${NO_COST_TEXT} De visas därför inte. Fyll i inköpspriset i artikelregistret, så räknar verktyget fram bruttovinst och marginal.`});
  }else if((s.noCostArticles.size || s.noCostServiceArticles.size) && s.profit > 0){
    const share = s.noCostShare;
    if(share >= 2){
      const adjEx     = s.revenueEx - s.noCostRevenueEx;
      const adjMargin = div(s.profit - s.noCostProfit, adjEx) * 100;
      const parts = [];
      if(s.noCostArticles.size)        parts.push(`${num(s.noCostArticles.size)} varuartiklar`);
      if(s.noCostServiceArticles.size) parts.push(`${num(s.noCostServiceArticles.size)} tjänster`);
      notes.push({tone:'warn', h:'Delar av marginalen saknar inköpspris',
        p:`<b>${parts.join(' och ')}</b> saknar inköpspris i artikelregistret. De omsätter ${kr(s.noCostRevenue)} som räknas som nästan ren vinst, alltså <b>${pct(share)}</b> av bruttovinsten. ` +
          // Har inget i urvalet inköpspris finns ingen marginal att räkna om till
          (adjEx > 0 ? `Utan dem blir marginalen omkring ${pct(adjMargin)} i stället för ${pct(s.margin)}. `
                     : 'Inget i urvalet har inköpspris, så marginalen här går inte att lita på. ') +
          'Fyll i inköpspriset på de artiklarna så blir all marginalanalys rättvisande.'});
    }
  }

  // Kvittoleverans: en iakttagelse om hur kvittona lämnar kassan. Bara det
  // filen visar — inte vad kunden hade velat eller vad butiken går miste om.
  if(s.tx >= 100){
    // Ett kvitto är ett köp, inte en kund — samma kund kan handla flera gånger
    const DEL = [['TRASH_CAN', 'valde kunden bort kvittot'], ['PRINTER', 'levererades det på papper'],
                 ['EMAIL', 'levererades det via e-post'], ['UNKNOWN', 'saknas uppgift om kvittot']];
    const delar = DEL.map(([k, text]) => ({k, text, n: s.delivery.get(k)?.count || 0}))
      .filter(d => d.n > 0).sort((a,b) => b.n - a.n);
    // Under en promille blir procenten 0,0 %; då är antalet tydligare
    const hur = n => n / s.tx < 0.001 ? `${num(n)} köp` : pct(n / s.tx * 100);
    const satser = delar.map((d, i) => `vid <b>${hur(d.n)}</b>${i === 0 && d.n / s.tx >= 0.001 ? ' av köpen' : ''} ${d.text}`);
    const lista = satser.length > 1 ? satser.slice(0, -1).join(', ') + ' och ' + satser[satser.length - 1] : satser[0];
    if(lista){
      const papper = s.printShare >= 20
        ? ` Papperskvitton kostar pengar. Fråga gärna om kunden vill ha kvittot, eller erbjud det via e-post.` : '';
      notes.push({tone:'', h:'Så levereras kvitton i kassan',
        p: lista.charAt(0).toUpperCase() + lista.slice(1) + '.' + papper});
    }
  }

  const dEmail = s.delivery.get('EMAIL'), dPrint = s.delivery.get('PRINTER');
  if(dEmail && dEmail.count >= 10 && dPrint && dPrint.count >= 10){
    const aovEmail = div(dEmail.revenue, dEmail.count);
    const aovPrint = div(dPrint.revenue, dPrint.count);
    if(aovEmail > aovPrint * 1.5){
      notes.push({tone:'', h:'Större köp får oftare kvittot via e-post',
        p:`Ett köp med e-postkvitto är i snitt <b>${kr(aovEmail)}</b>, mot ${kr(aovPrint)} för ett köp med papperskvitto.`});
    }
  }

  // Leverantör saknas på en stor del av sortimentet. Saknas den på allt har
  // butiken valt att inte använda leverantörer — då är filtret avstängt och
  // det finns inget att påpeka.
  const gap = state.lineFiltered ? null : supplierGap(s);
  if(gap){
    notes.push({tone:'warn', h:'Stora delar av sortimentet saknar leverantör',
      // Avrundat till 100,0 % vore motsägelsefullt: någon rad har ju leverantör
      p:`${pct(gap.share) === pct(100) ? '<b>Nästan hela</b> omsättningen' : `<b>${pct(gap.share)}</b> av omsättningen`} kommer från ${num(gap.articles)} artiklar som saknar leverantör i artikelregistret. Marginal per leverantör och urvalet på leverantör visar därför bara en del av försäljningen. Välj <b>Saknar leverantör</b> under Leverantör för att se vilka artiklarna är.`});
  }

  // Bästa veckodag
  // Med veckodagar eller klockslag valda gäller iakttagelsen bara dem
  const wd = [...s.byWeekday.entries()].sort((a,b) => b[1].revenue - a[1].revenue)[0];
  if(wd && s.byWeekday.size > 1){
    const share = div(wd[1].revenue, s.revenue)*100;
    notes.push({tone:'', h: state.dayTime
      ? `${WEEKDAYS[wd[0]]} är starkast i urvalet (${state.dayTime.charAt(0).toLowerCase() + state.dayTime.slice(1)})`
      : `${WEEKDAYS[wd[0]]} är din starkaste dag`,
      p:`${WEEKDAYS[wd[0]]} står för <b>${pct(share)}</b> av omsättningen i perioden, ${kr(wd[1].revenue)} fördelat på ${num(wd[1].tx)} köp.`});
  }

  // Bästa timme
  const hr = [...s.byHour.entries()].sort((a,b) => b[1] - a[1])[0];
  if(hr){
    const tx = s.byHourTx.get(hr[0]) || 0;
    notes.push({tone:'', h:`Mest säljs mellan ${String(hr[0]).padStart(2,'0')} och ${String(hr[0]+1).padStart(2,'0')}${state.dayTime ? ' i urvalet' : ''}`,
      p:`Den timmen omsätter <b>${kr(hr[1])}</b> på ${num(tx)} köp. Planera bemanningen så att kassan är fullt bemannad då.`});
  }

  // Varor mot tjänster. Skillnaden är bara meningsfull om båda har inköpspris
  // registrerat — annars mäter man bokföringsvana, inte lönsamhet.
  if(state.hasCost && s.product.revenueEx > 0 && s.service.revenueEx > 0){
    const diff = s.service.margin - s.product.margin;
    const svcBlind  = s.service.noCostShare;
    const goodsBlind = s.product.noCostShare;

    if(svcBlind >= 50 || goodsBlind >= 50){
      const which = svcBlind >= 50 ? 'tjänsterna' : 'varorna';
      const blind = svcBlind >= 50 ? svcBlind : goodsBlind;
      notes.push({tone:'warn', h:'Varor och tjänster går inte att jämföra rakt av',
        p:`Tjänster ligger på ${pct(s.service.margin)} och varor på ${pct(s.product.margin)}, men <b>${pct(blind)}</b> av försäljningen på ${which} saknar registrerat inköpspris. Den skillnaden speglar därför mest vad som är ifyllt i artikelregistret, inte vad som faktiskt är mest lönsamt.`});
    }else if(Math.abs(diff) >= 5){
      const better = diff > 0 ? 'Tjänster' : 'Varor';
      const worse  = diff > 0 ? 'varor' : 'tjänster';
      notes.push({tone:'good', h:`${better} ger bättre marginal`,
        p:`${better} ligger på <b>${pct(diff > 0 ? s.service.margin : s.product.margin)}</b> mot ${pct(diff > 0 ? s.product.margin : s.service.margin)} för ${worse}, en skillnad på ${nf1.format(Math.abs(diff)).replace('.',',')} procentenheter. Båda har inköpspris registrerat, så jämförelsen håller.`});
    }
  }

  // Koncentration
  const top = [...s.products.values()].sort((a,b) => b.profit - a.profit);
  if(state.hasCost && top.length >= 10 && s.profit > 0){
    const t10 = top.slice(0,10).reduce((a,p) => a + p.profit, 0);
    const share = div(t10, s.profit)*100;
    if(share >= 40){
      notes.push({tone:'warn', h:'Vinsten vilar på få artiklar',
        p:`Tio artiklar av ${num(s.products.size)} står för <b>${pct(share)}</b> av bruttovinsten. Säljer någon av dem sämre märks det direkt på resultatet.`});
    }
  }

  // Lågmarginalartiklar med volym
  const lowVol = [...s.products.values()]
    .filter(p => p.units >= CFG.MIN_QTY_LOW_MARGIN && p.revenueEx > 0 && (p.profit/p.revenueEx*100) < 15);
  if(state.hasCost && lowVol.length){
    const rev = lowVol.reduce((a,p) => a + p.revenue, 0);
    notes.push({tone:'warn', h:`${num(lowVol.length)} artiklar säljer bra men ger låg marginal`,
      p:`De omsätter tillsammans <b>${kr(rev)}</b> men ligger under 15 % marginal. Se dem i tabellen Säljer bra men ger lägst marginal längre ned, eller sortera på marginal under Produkter.`});
  }

  // Returer
  if(s.returnValue < 0){
    const share = div(Math.abs(s.returnValue), s.revenue + Math.abs(s.returnValue))*100;
    notes.push({tone: share >= 5 ? 'warn' : '', h:'Returer i perioden',
      p:`Returer motsvarar <b>${kr(Math.abs(s.returnValue))}</b>, ungefär ${pct(share)} av bruttoförsäljningen, fördelat på ${num(s.returnReceipts)} kvitton.`});
  }

  // Rabatter: kampanjer och rabatt på artikel var för sig
  const disc = [...s.products.values()].reduce((a,p) => a + p.discount, 0);
  if(disc > 0){
    const split = s.promoDiscount > 0 && s.articleDiscount > 0
      ? ` Av det är ${kr(s.promoDiscount)} kampanjer och ${kr(s.articleDiscount)} extrapris eller rabatt på artikel, se fliken Kampanjer &amp; rabatter.`
      : '';
    notes.push({tone:'', h:'Lämnade rabatter',
      p:`Under perioden har du lämnat <b>${kr(disc)}</b> i rabatt, vilket motsvarar ${pct(div(disc, s.revenue + disc)*100)} av försäljningen före rabatt.${split}`});
  }

  // Kunder i lojalitetsprogrammet. Bara det filen visar: hur stor del av
  // köpen och försäljningen som har en kund, och snittköpet med och utan.
  if(state.hasCustomers && !customerFiltered() && s.custTx >= 10 && s.otherTx >= 10){
    notes.push({tone:'', h:'Köp med kund i lojalitetsprogrammet',
      p:`<b>${pct(s.custShare)}</b> av köpen har en kund vald, och de står för <b>${pct(s.custRevenueShare)}</b> av omsättningen. ` +
        `Ett köp med kund är i snitt ${kr(s.custAov)}, mot ${kr(s.otherAov)} utan kund.`});
    const before = state.prev?.stats;
    if(before && before.tx >= 50 && s.tx >= 50){
      const d = s.custShare - before.custShare;
      if(Math.abs(d) >= 5){
        notes.push({tone:'', h:`Andelen köp med kund har ${d > 0 ? 'ökat' : 'minskat'}`,
          p:`<b>${pct(s.custShare)}</b> av köpen har en kund vald, mot ${pct(before.custShare)} i jämförelseperioden (${esc(state.prev.label)}). ` +
            'Se utvecklingen under Utveckling, måttet Köp med kund.'});
      }
    }
  }
  if(state.hasBonus && !state.lineFiltered && s.bonusCount > 0){
    notes.push({tone:'', h:'Inlösta bonuscheckar',
      p:`<b>${num(s.bonusCount)}</b> ${s.bonusCount === 1 ? 'bonuscheck' : 'bonuscheckar'} löstes in för ${kr(s.bonusAmount)}. ` +
        `Utöver checken betalade kunden i snitt ${kr(s.bonusPaidAvg)}.`});
  }

  // Snittköp mot dagssnitt
  if(s.days >= 7){
    const daily = [...s.byDay.values()].map(d => d.revenue).sort((a,b) => a-b);
    const median = daily[Math.floor(daily.length/2)];
    notes.push({tone:'', h:'Så ser en normal dag ut',
      p:`Mediandagen omsätter <b>${kr(median)}</b>. Snittet ligger på ${kr(s.revenuePerDay)}. Ligger snittet klart över medianen är det enstaka toppdagar som drar upp siffran.`});
  }

  box.innerHTML = notes.length
    ? notes.map(n => `<div class="note ${n.tone}"><h4>${esc(n.h)}</h4><p>${n.p}</p></div>`).join('')
    : `<div class="empty"><strong>För lite data för iakttagelser</strong>Välj en längre period.</div>`;
}
